import React, { useCallback, useEffect, useMemo } from "react";
import { useFlow } from "@/contexts/FlowContext";
import { useModel } from "@/contexts/ModelContext";
import { useGetPredictions } from "@/hooks/useGetPredictions";
import { useComputedValues } from "@/contexts/ComputedValuesContext";
import { useCurrentMedia, useSyncPromptLabels, useCurrentWorkspace, useMediaStore } from "@/store";
import {
    separateLabelsByDiffStatus,
    buildBoxPromptingDataForDiffLabels
} from "@/hooks/useAutoPredictions/utils";
import { InferenceTypes } from "@functions/rapid/services/inference/stream/types";
import { GeneratePredictionsBtn } from "./GeneratePredictionsBtn";
import { TextPrompt } from "./TextPrompt";
import { TextPromptProposalChips } from "./TextPromptProposalChips";
import { useAsyncBoundOperation } from "@/hooks/useApi";
import { ErrorInfo } from "@/components/ErrorInfo";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { AnalyticsFlowEvent } from "@/analytics/types";
import { getAutoInvokeStatus, setAutoInvokeStatus } from "@/utils/autoInvokeStatus";
import _ from "lodash";
import { useIsGeneratingPredictions } from "@/hooks/useIsGeneratingPredictions";
import { useKeyboardShortcut } from "@/hooks/useKeyboardShortcut";
import { useAutoPredictionsTrigger } from "@/contexts/AutoPredictionsContext";
import { deleteAnnotationsByLabelCompletely } from "../PromptLabel/Cards/cardUtils";

interface GeneratePredictionsProps {
    disabled?: boolean;
}

export const GeneratePredictions = ({ disabled = false }: GeneratePredictionsProps) => {
    const { setFlowLocked, setCurrentFrameAnnotations } = useFlow();
    const { getPredictionsForFile } = useGetPredictions();
    const currentMedia = useCurrentMedia();
    const workspaceUrl = useCurrentWorkspace()?.url ?? "anonymous";
    const media = useMediaStore();
    const {
        setAnnotationsForFileAndFrameId,
        annotationsForFileAndFrameId,
        textPrompts,
        promptLabels,
        setTextPromptStr,
        updatePromptLabels
    } = useModel();
    const { currentFramePredictions, currentFrameAnnotations } = useComputedValues();
    const syncPromptLabels = useSyncPromptLabels();
    const { track } = useAnalytics();
    const { registerTrigger } = useAutoPredictionsTrigger();

    // Check if there are any drawn annotations
    const hasDrawnAnnotations = currentFrameAnnotations.length > 0;

    // Check if there are any draft annotations
    const hasDraftAnnotations = currentFrameAnnotations.some((anno) => anno.draft);

    // Check if there are object cards
    const hasObjectCards = promptLabels.size > 0;

    // Text prompt handler with mixed inference support
    // - Labels WITHOUT diffs (no user edits): use SAM3 text prompting
    // - Labels WITH diffs (user has edited/drawn boxes): use OWLv2 box prompting
    const handleGeneratePredictions = useCallback(async () => {
        setFlowLocked(true);

        // Identify NEW text prompts (not already in promptLabels)
        const existingLabelNames = new Set(Array.from(promptLabels.keys()));
        const newTextPrompts = textPrompts.filter((prompt) => !existingLabelNames.has(prompt));
        const hasExistingLabels = promptLabels.size > 0;

        // Combined prompts for syncing
        const combinedTextPrompts = _.uniq([
            ...textPrompts,
            ...Array.from(promptLabels.values()).map((label) => label.name)
        ]);

        // Check if we need to delete a label after predictions (from NoResultsEmptyState chip click)
        const labelToDelete = sessionStorage.getItem("labelToDeleteAfterPredictions");

        syncPromptLabels(combinedTextPrompts);

        try {
            await new Promise<void>(async (resolve, reject) => {
                if (!currentMedia?.file.metadata?.naturalDimensions || !currentMedia?.frame) {
                    resolve();
                    return;
                }

                const mediaItemToUse = currentMedia?.file.isVideo
                    ? currentMedia?.frame!
                    : currentMedia?.file!;
                const extractedFrameMeta = currentMedia?.file.isVideo
                    ? {
                          videoId: currentMedia?.file.id,
                          segment: currentMedia?.frame?.metadata?.segment!
                      }
                    : undefined;

                try {
                    // If we have existing labels and new text prompts, run predictions only for new classes
                    // This appends to existing annotations instead of replacing them
                    if (hasExistingLabels && newTextPrompts.length > 0) {
                        // Run predictions for each new label in single-label mode (appends)
                        for (const newLabel of newTextPrompts) {
                            if (currentMedia?.file.isVideo) {
                                if (typeof currentMedia?.frame?.metadata?.segment !== "number") {
                                    continue;
                                }
                            }

                            await getPredictionsForFile({
                                mediaItem: mediaItemToUse,
                                extractedFrameMeta,
                                inferenceType: InferenceTypes.SAM3,
                                textPrompts: [newLabel],
                                isDraft: true,
                                targetLabel: newLabel
                            });
                        }
                        resolve();
                    } else {
                        // Full prediction for all prompts - use mixed inference based on hasDiffs
                        if (
                            currentMedia?.file.isVideo &&
                            typeof currentMedia?.frame?.metadata?.segment !== "number"
                        ) {
                            resolve();
                            return;
                        }

                        // Separate labels by diff status for mixed inference
                        const { labelsWithDiffs, labelsWithoutDiffs } =
                            separateLabelsByDiffStatus(promptLabels);

                        // All NEW text prompts go to SAM3 (they don't have diffs yet)
                        const sam3Labels = _.uniq([...labelsWithoutDiffs, ...newTextPrompts]);

                        // Debug log
                        console.log("[GeneratePredictions] Mixed inference:", {
                            allLabels: Array.from(promptLabels.entries()).map(([name, label]) => ({
                                name,
                                hasDiffs: label.hasDiffs
                            })),
                            labelsWithDiffs,
                            labelsWithoutDiffs,
                            sam3Labels,
                            newTextPrompts
                        });

                        const inferencePromises: Promise<void>[] = [];

                        // Build box prompting data first to determine if we're running mixed inference
                        let boxPromptingData: any[] = [];
                        let boxConfidenceThreshold = 0;

                        if (labelsWithDiffs.length > 0) {
                            // Build media dimensions map for box prompting
                            const mediaDimensions = new Map<
                                string,
                                { width: number; height: number }
                            >();
                            const defaultDimensions =
                                currentMedia?.file.metadata?.naturalDimensions ||
                                currentMedia?.file.metadata?.serverMediaDimensions;

                            // Add dimensions for all frames with annotations
                            Object.entries(annotationsForFileAndFrameId).forEach(
                                ([fileId, frames]) => {
                                    Object.keys(frames).forEach((frameId) => {
                                        if (!mediaDimensions.has(frameId)) {
                                            const mediaItem = media[fileId];
                                            const dimensions =
                                                mediaItem?.metadata?.naturalDimensions ||
                                                mediaItem?.metadata?.serverMediaDimensions ||
                                                defaultDimensions;
                                            if (dimensions) {
                                                mediaDimensions.set(frameId, dimensions);
                                            }
                                        }
                                    });
                                }
                            );

                            // Build box prompting data
                            const result = buildBoxPromptingDataForDiffLabels(
                                annotationsForFileAndFrameId,
                                labelsWithDiffs,
                                mediaDimensions,
                                workspaceUrl
                            );
                            boxPromptingData = result.promptingData;
                            boxConfidenceThreshold = result.confidenceThreshold;
                        }

                        // Determine if we're running mixed inference (both SAM3 and OWLv2)
                        const isMixedInference =
                            sam3Labels.length > 0 &&
                            boxPromptingData.length > 0 &&
                            labelsWithDiffs.length > 0;

                        // SAM3 text prompting for labels WITHOUT diffs
                        if (sam3Labels.length > 0) {
                            console.log(
                                "[GeneratePredictions] Starting SAM3 text prompting for:",
                                sam3Labels
                            );
                            inferencePromises.push(
                                getPredictionsForFile({
                                    mediaItem: mediaItemToUse,
                                    extractedFrameMeta,
                                    inferenceType: InferenceTypes.SAM3,
                                    textPrompts: sam3Labels,
                                    isDraft: true,
                                    // Use append mode when running mixed inference to avoid race conditions
                                    appendMode: isMixedInference
                                })
                            );
                        }

                        // OWLv2 box prompting for labels WITH diffs
                        if (boxPromptingData.length > 0 && labelsWithDiffs.length > 0) {
                            console.log(
                                "[GeneratePredictions] Starting OWLv2 box prompting for:",
                                labelsWithDiffs,
                                "with confidence threshold:",
                                boxConfidenceThreshold
                            );
                            inferencePromises.push(
                                getPredictionsForFile({
                                    mediaItem: mediaItemToUse,
                                    extractedFrameMeta,
                                    inferenceType: InferenceTypes.OWLVIT,
                                    customPromptingData: boxPromptingData,
                                    textPrompts: labelsWithDiffs,
                                    confidenceThreshold: boxConfidenceThreshold,
                                    isDraft: true,
                                    // Use append mode when running mixed inference to avoid race conditions
                                    appendMode: isMixedInference
                                })
                            );
                        }

                        // Run inference types in parallel
                        await Promise.all(inferencePromises);
                    }
                    setTextPromptStr("");
                    // No approve phase, so unlock immediately
                    setFlowLocked(false);

                    // Delete the source label if one was stored (from NoResultsEmptyState chip click)
                    if (labelToDelete) {
                        // Delete all annotations for this label across all files
                        setAnnotationsForFileAndFrameId((prev) => {
                            return deleteAnnotationsByLabelCompletely(prev, labelToDelete);
                        });

                        // Calculate remaining label names
                        const remainingLabelNames = Array.from(promptLabels.keys()).filter(
                            (labelName) => labelName !== labelToDelete
                        );

                        // Update prompt labels to remove the deleted label
                        updatePromptLabels([], [labelToDelete]);

                        // Sync prompt labels to ensure consistency
                        setTimeout(() => {
                            syncPromptLabels(remainingLabelNames);
                        }, 0);

                        // Force a canvas redraw
                        setTimeout(() => {
                            setCurrentFrameAnnotations((prev: any) => {
                                return [...prev];
                            });
                        }, 50);

                        // Clear the stored label
                        sessionStorage.removeItem("labelToDeleteAfterPredictions");
                    }

                    resolve();
                } catch (error) {
                    reject(error);
                }
            });
            // on success, keep locked (PromptSidebarContent will manage unlocking based on predictions)
        } catch (e) {
            console.error(e);
            // On error, unlock so user can retry
            setFlowLocked(false);
            throw e;
        }
    }, [
        currentMedia,
        getPredictionsForFile,
        textPrompts,
        setFlowLocked,
        syncPromptLabels,
        setAnnotationsForFileAndFrameId,
        annotationsForFileAndFrameId,
        promptLabels,
        setTextPromptStr,
        updatePromptLabels,
        setCurrentFrameAnnotations,
        workspaceUrl,
        media
    ]);

    const handleSmartClickSwitcher = useCallback(async () => {
        if (textPrompts.length > 0) {
            // If there are text prompts, use text prompt generation
            await handleGeneratePredictions();
        } else if (hasObjectCards) {
            // If there are object cards but no text prompts, use text prompt generation with existing labels
            await handleGeneratePredictions();
        }
    }, [handleGeneratePredictions, hasObjectCards, textPrompts.length]);

    const {
        error: errorGeneratePredictions,
        reset: resetGeneratePredictions,
        invoke: invokeGeneratePredictions
    } = useAsyncBoundOperation(handleSmartClickSwitcher);

    // Combine loading states - either manual generation or auto-generation
    const isGeneratingPredictions = useIsGeneratingPredictions();

    // Auto-invoke predictions only once when first entering the build step
    useEffect(() => {
        const shouldAutoInvoke =
            !getAutoInvokeStatus() &&
            textPrompts.length > 0 &&
            currentFramePredictions.length === 0 &&
            !isGeneratingPredictions &&
            !hasDrawnAnnotations &&
            !hasDraftAnnotations && // Don't auto-invoke if there are draft annotations (predictions were already generated)
            currentMedia?.file.metadata?.naturalDimensions &&
            currentMedia?.frame;

        if (shouldAutoInvoke) {
            setAutoInvokeStatus(true);
            invokeGeneratePredictions();
        }
    }, [
        textPrompts.length,
        currentFramePredictions.length,
        isGeneratingPredictions,
        hasDrawnAnnotations,
        hasDraftAnnotations,
        currentMedia?.file.metadata?.naturalDimensions,
        currentMedia?.frame,
        invokeGeneratePredictions
    ]);

    const handleClick = useCallback(
        (e: React.MouseEvent) => {
            // Track generate predictions button click
            track({
                event: AnalyticsFlowEvent.GENERATE_PREDICTIONS_BUTTON_CLICKED,
                data: {
                    triggerMethod: "button_click",
                    hasDrawnAnnotations: hasDrawnAnnotations,
                    hasTextPrompts: textPrompts.length > 0,
                    hasObjectCards: hasObjectCards,
                    textPrompts: textPrompts,
                    annotationCount: currentFrameAnnotations.length
                }
            });

            invokeGeneratePredictions();
            e.preventDefault();
        },
        [
            invokeGeneratePredictions,
            track,
            hasDrawnAnnotations,
            textPrompts,
            hasObjectCards,
            currentFrameAnnotations.length
        ]
    );

    // Form submit handler
    const handleSubmit = useCallback(
        (e: React.FormEvent) => {
            // Track generate predictions form submit
            track({
                event: AnalyticsFlowEvent.GENERATE_PREDICTIONS_BUTTON_CLICKED,
                data: {
                    triggerMethod: "form_submit",
                    hasDrawnAnnotations: hasDrawnAnnotations,
                    hasTextPrompts: textPrompts.length > 0,
                    hasObjectCards: hasObjectCards,
                    textPrompts: textPrompts,
                    annotationCount: currentFrameAnnotations.length
                }
            });

            invokeGeneratePredictions();
            e.preventDefault();
        },
        [
            invokeGeneratePredictions,
            track,
            hasDrawnAnnotations,
            textPrompts,
            hasObjectCards,
            currentFrameAnnotations.length
        ]
    );

    const handleRetry = useCallback(
        (e: React.MouseEvent) => {
            resetGeneratePredictions();
            invokeGeneratePredictions();
            e.preventDefault();
        },
        [invokeGeneratePredictions, resetGeneratePredictions]
    );

    // Keyboard shortcut: Enter to generate predictions
    useKeyboardShortcut({
        key: "Enter",
        handler: () => {
            // Track generate predictions keyboard shortcut
            track({
                event: AnalyticsFlowEvent.GENERATE_PREDICTIONS_BUTTON_CLICKED,
                data: {
                    triggerMethod: "keyboard_shortcut",
                    hasDrawnAnnotations: hasDrawnAnnotations,
                    hasTextPrompts: textPrompts.length > 0,
                    hasObjectCards: hasObjectCards,
                    textPrompts: textPrompts,
                    annotationCount: currentFrameAnnotations.length
                }
            });

            invokeGeneratePredictions();
        },
        requireMeta: false
    });

    useEffect(() => {
        registerTrigger(invokeGeneratePredictions);
    }, [registerTrigger, invokeGeneratePredictions]);

    // Determine if button should be disabled
    // Button is only disabled during generation or if there's nothing to generate
    const isDisabled =
        isGeneratingPredictions || disabled || (!textPrompts.length && !hasObjectCards);
    // Note: We allow generating predictions at any time - new text prompts will append to existing annotations

    const showTextPromptProposalChips = !hasObjectCards;

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="prompt-container">
                <TextPrompt />
                <GeneratePredictionsBtn disabled={isDisabled} onClick={handleClick} />
                {errorGeneratePredictions && (
                    <ErrorInfo error={errorGeneratePredictions} onRetryClick={handleRetry} />
                )}
            </div>
            {showTextPromptProposalChips && (
                <TextPromptProposalChips
                    isDisabled={isGeneratingPredictions}
                    showPoweredBy={false}
                />
            )}
        </form>
    );
};
